import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import VerifyEmailNeededPage from "@/pages/VerifyEmailNeededPage";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import ContactPage from "@/pages/legal/ContactPage";
import AppShell from "@/components/AppShell";
import AppIndexRedirect from "@/pages/AppIndexRedirect";
import TeamsPage from "@/pages/TeamsPage";
import EmployeesPage from "@/pages/EmployeesPage";
import SkillsPage from "@/pages/SkillsPage";
import RosterPage from "@/pages/RosterPage";
import MyRosterPage from "@/pages/MyRosterPage";
import SettingsPage from "@/pages/SettingsPage";
import AccountPage from "@/pages/AccountPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CategoriesPage from "@/pages/CategoriesPage";
import LocationsPage from "@/pages/LocationsPage";
import AssignmentRulesPage from "@/pages/AssignmentRulesPage";
import PoliciesPage from "@/pages/PoliciesPage";
import KPIsPage from "@/pages/KPIsPage";
import PerformanceFocusPage from "@/pages/PerformanceFocusPage";
import AutomationPreviewPage from "@/pages/AutomationPreviewPage";
import AutomationPage from "@/pages/AutomationPage";
import DraftsReviewPage from "@/pages/DraftsReviewPage";
import DraftsRotaListPage from "@/pages/DraftsRotaListPage";
import DraftsRotaReviewPage from "@/pages/DraftsRotaReviewPage";
import HistoricalReplayPage from "@/pages/HistoricalReplayPage";
import AutomationReadinessPage from "@/pages/AutomationReadinessPage";
import SetupHubPage from "@/pages/SetupHubPage";
import SetupAssistantPage from "@/pages/SetupAssistantPage";
import AdvisorySettingsPage from "@/pages/AdvisorySettingsPage";
import TemplatesPage from "@/pages/TemplatesPage";
import ShiftTemplatesPage from "@/pages/ShiftTemplatesPage";
import ShiftPatternsPage from "@/pages/ShiftPatternsPage";
import WorkingPatternsPage from "@/pages/WorkingPatternsPage";
import RotaStylePage from "@/pages/RotaStylePage";
import HomePage from "@/pages/public/HomePage";
import FeaturesPage from "@/pages/public/FeaturesPage";
import PricingPage from "@/pages/public/PricingPage";
import AboutPage from "@/pages/public/AboutPage";
import TrialSignupPage from "@/pages/public/TrialSignupPage";
import AdminCompaniesPage from "@/pages/admin/AdminCompaniesPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminSimulationsPage from "@/pages/admin/AdminSimulationsPage";
import AutomationSettingsPage from "@/pages/AutomationSettingsPage";
import RotaStyleAutomationPage from "@/pages/RotaStyleAutomationPage";
import BankHolidaysPage from "@/pages/BankHolidaysPage";
import LeavePage from "@/pages/LeavePage";
import ReportsPage from "@/pages/ReportsPage";
import AuditPage from "@/pages/AuditPage";
import ImportRosterPage from "@/pages/ImportRosterPage";
import SwapsPage from "@/pages/SwapsPage";
import BillingPage from "@/pages/BillingPage";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-[#F6F9FE]">
      <div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#2563EB] animate-spin"/>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    // TP-AT.RS.8.8 (Feb 2026) -- Workspace / Team admins who land
    // on the super_admin-only legacy `/app/drafts*` routes go to
    // Roster (their modern flow lands drafts directly there).
    return <Navigate to="/app/roster" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function Router() {
  return (
    <Routes>
      {/* Public marketing pages */}
      <Route path="/" element={<HomePage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/start" element={<PublicOnly><TrialSignupPage /></PublicOnly>} />

      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<Navigate to="/start" replace />} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email-needed" element={<VerifyEmailNeededPage />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<AppIndexRedirect />} />
        <Route path="teams" element={<Protected roles={["company_admin","super_admin"]}><TeamsPage /></Protected>} />
        <Route path="employees" element={<Protected roles={["company_admin","team_admin","super_admin"]}><EmployeesPage /></Protected>} />
        <Route path="skills" element={<Protected roles={["company_admin","team_admin","super_admin"]}><SkillsPage /></Protected>} />
        <Route path="roster" element={<Protected roles={["company_admin","team_admin","super_admin"]}><RosterPage /></Protected>} />
        <Route path="my-roster" element={<MyRosterPage />} />
        <Route path="leave" element={<LeavePage />} />
        <Route path="reports" element={<Protected roles={["company_admin","team_admin","super_admin"]}><ReportsPage /></Protected>} />
        <Route path="activity" element={<Protected roles={["company_admin","team_admin","super_admin"]}><AuditPage /></Protected>} />
        <Route path="categories" element={<Protected roles={["company_admin","super_admin"]}><CategoriesPage /></Protected>} />
        <Route path="locations" element={<Protected roles={["company_admin","team_admin","super_admin","employee"]}><LocationsPage /></Protected>} />
        <Route path="rules" element={<Protected roles={["company_admin","team_admin","super_admin"]}><AssignmentRulesPage /></Protected>} />
        <Route path="policies" element={<Protected roles={["company_admin","team_admin","super_admin"]}><PoliciesPage /></Protected>} />
        <Route path="kpis" element={<Protected roles={["company_admin","team_admin","super_admin"]}><KPIsPage /></Protected>} />
        <Route path="performance" element={<Protected roles={["company_admin","team_admin","super_admin"]}><PerformanceFocusPage /></Protected>} />
        {/* Phase 2Z.x — the legacy automation engine is retired from
            customer-facing routes. Redirect to the current Autopilot
            module on Rota Style. The underlying AutomationPage component
            is intentionally left importable for now to keep the import
            graph byte-stable; it is simply no longer reachable from
            customer-facing navigation. */}
        <Route path="automation" element={<Navigate to="/app/rota-style/automation" replace />} />
        <Route path="automation-insights" element={<Protected roles={["company_admin","team_admin","super_admin"]}><AutomationPreviewPage /></Protected>} />
        <Route path="drafts" element={<Protected roles={["super_admin"]}><DraftsReviewPage /></Protected>} />
        <Route path="drafts/rota" element={<Protected roles={["super_admin"]}><DraftsRotaListPage /></Protected>} />
        <Route path="drafts/rota/:period_id" element={<Protected roles={["super_admin"]}><DraftsRotaReviewPage /></Protected>} />
        <Route path="replays" element={<Protected roles={["company_admin","super_admin"]}><HistoricalReplayPage /></Protected>} />
        <Route path="readiness" element={<Protected roles={["company_admin","super_admin"]}><AutomationReadinessPage /></Protected>} />
        <Route path="setup" element={<Protected roles={["company_admin","super_admin"]}>{
          // Phase 2Z.k — Guided Setup Assistant. Company admins land
          // on the premium 11-step assistant; super-admins keep their
          // diagnostic Setup Hub via /app/setup/_legacy.
          JSON.parse(localStorage.getItem("nextrota_user") || "{}").role === "super_admin"
            ? <SetupHubPage />
            : <SetupAssistantPage />
        }</Protected>} />
        <Route path="setup/_legacy" element={<Protected roles={["super_admin"]}><SetupHubPage /></Protected>} />
        <Route path="rota-style" element={<Protected roles={["company_admin","team_admin","super_admin"]}><RotaStylePage /></Protected>} />
        <Route path="rota-style/automation" element={<Protected roles={["company_admin","super_admin"]}><RotaStyleAutomationPage /></Protected>} />
        <Route path="advisory" element={<Protected roles={["company_admin","team_admin","super_admin"]}><AdvisorySettingsPage /></Protected>} />
        <Route path="templates" element={<Protected roles={["company_admin","super_admin"]}><TemplatesPage /></Protected>} />
        <Route path="shift-templates" element={<Protected roles={["company_admin","team_admin","super_admin"]}><ShiftTemplatesPage /></Protected>} />
        <Route path="shift-patterns" element={<Protected roles={["company_admin","team_admin","super_admin"]}><ShiftPatternsPage /></Protected>} />
        <Route path="working-patterns" element={<Protected roles={["company_admin","team_admin","super_admin"]}><WorkingPatternsPage /></Protected>} />
        <Route path="bank-holidays" element={<Protected roles={["company_admin","super_admin"]}><BankHolidaysPage /></Protected>} />
        <Route path="import" element={<Protected roles={["company_admin"]}><ImportRosterPage /></Protected>} />
        <Route path="swaps" element={<Protected roles={["company_admin","team_admin","employee"]}><SwapsPage /></Protected>} />
        <Route path="settings" element={<Protected roles={["company_admin","super_admin"]}><SettingsPage /></Protected>} />
        <Route path="billing" element={<Protected roles={["company_admin"]}><BillingPage /></Protected>} />
        <Route path="account" element={<AccountPage />} />
        <Route path="companies" element={<Protected roles={["super_admin"]}><CompaniesPage /></Protected>} />
        {/* Super Admin CRM */}
        <Route path="admin/companies" element={<Protected roles={["super_admin"]}><AdminCompaniesPage /></Protected>} />
        <Route path="admin/users" element={<Protected roles={["super_admin"]}><AdminUsersPage /></Protected>} />
        {/* Hidden engineering-only — Phase 2A simulation debug */}
        <Route path="admin/simulations" element={<Protected roles={["company_admin","super_admin"]}><AdminSimulationsPage /></Protected>} />
        <Route path="automation-settings" element={<Navigate to="/app/automation#advanced" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <Router />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}
